import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { API, getStudentId } from "@/lib/api";

const SESSION_KEY = "smartguru_session_id";
const HEARTBEAT_MS = 25000;

const moduleFromPath = (pathname) => {
  if (pathname.startsWith("/tutor/learn")) return "Learn";
  if (pathname.startsWith("/tutor/homework")) return "Homework Help";
  if (pathname.startsWith("/tutor/explore")) return "Explore";
  if (pathname.startsWith("/tutor/revise") || pathname.startsWith("/mastery")) return "Revise";
  if (pathname.startsWith("/practice")) return "Practice";
  if (pathname.startsWith("/assessment")) return "Assess";
  if (pathname.startsWith("/study")) return "Combine Study";
  return null;
};

const getSessionId = () => {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const sendHeartbeat = (studentId, sessionId, module, seconds, useBeacon) => {
  if (seconds <= 0) return;
  const payload = JSON.stringify({ student_id: studentId, session_id: sessionId, module, seconds });
  const url = `${API}/analytics/heartbeat`;
  if (useBeacon && navigator.sendBeacon) {
    navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
  } else {
    fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: payload, keepalive: true }).catch(() => {});
  }
};

export const AnalyticsTracker = () => {
  const location = useLocation();
  const moduleRef = useRef(moduleFromPath(location.pathname));
  const accumulatedRef = useRef(0);
  const identityRef = useRef(null);

  const flush = (useBeacon = false) => {
    const identity = identityRef.current;
    if (!identity) return;
    const seconds = accumulatedRef.current;
    accumulatedRef.current = 0;
    sendHeartbeat(identity.studentId, identity.sessionId, moduleRef.current, seconds, useBeacon);
  };

  useEffect(() => {
    const newModule = moduleFromPath(location.pathname);
    if (newModule !== moduleRef.current) {
      flush(false);
      moduleRef.current = newModule;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const studentId = getStudentId();
    if (!studentId) return;
    identityRef.current = { studentId, sessionId: getSessionId() };

    const tick = setInterval(() => {
      if (document.visibilityState === "visible") accumulatedRef.current += 1;
    }, 1000);

    const flushInterval = setInterval(() => flush(false), HEARTBEAT_MS);
    const handlePageHide = () => flush(true);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handlePageHide);

    return () => {
      clearInterval(tick);
      clearInterval(flushInterval);
      flush(true);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handlePageHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};
